import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MessageCircleHeart, MapPin, ArrowLeft, Share2 } from "lucide-react";
import { publicApi, photoUrl, useAccount } from "@/lib/account";
import { AiBadge, MemberBadge } from "@/components/ProfileCard";
import InterestButton from "@/components/InterestButton";
import ShortlistButton from "@/components/ShortlistButton";
import useSeo from "@/lib/seo";
import { usePrefs } from "@/lib/prefs";
import { compatibility, crossBorder } from "@/lib/compat";

const Fact = ({ label, value }) => value ? (
  <div className="border-b border-slate-800 py-3 flex justify-between text-sm"><span className="text-slate-400">{label}</span><span className="text-slate-100">{value}</span></div>
) : null;

export default function ProfileDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const { isSignedIn, profile, account } = useAccount();
  const { t } = usePrefs();
  const navigate = useNavigate();
  useSeo(p ? `${p.name}, ${p.age} – ${p.profession}, ${p.city}` : "Profile", p ? `${p.religion} ${p.gender === "Female" ? "bride" : "groom"} from ${p.city}. ${p.about?.slice(0, 120)}` : undefined);
  useEffect(() => { publicApi.get(`/profiles/${id}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [id]);

  if (p === false) return <p className="text-center py-32 text-slate-400" data-testid="profile-not-found">Profile not found.</p>;
  if (!p) return <div className="py-32 text-center text-slate-500">Loading...</div>;
  const isMine = profile?.id === p.id;
  const startChat = () => navigate(isSignedIn ? `/chat/${p.id}` : "/sign-in");

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12" data-testid="profile-detail-page">
      <Link to="/browse" className="text-sm text-slate-400 hover:text-amber-200 flex items-center gap-2" data-testid="profile-back-link"><ArrowLeft className="w-4 h-4" /> {t("back")}</Link>
      <div className="grid lg:grid-cols-12 gap-10 mt-8">
        <div className="lg:col-span-6">
          <div className="rounded-3xl overflow-hidden aspect-[4/5] bg-[#0D0F15] card-royal">
            {p.photos?.length ? <img src={photoUrl(p.photos[active])} alt={p.name} className="w-full h-full object-cover object-top" data-testid="profile-main-photo" />
              : <div className="w-full h-full flex items-center justify-center font-display text-8xl text-amber-500/30">{p.name[0]}</div>}
          </div>
          {p.photos?.length > 1 && (
            <div className="flex gap-3 mt-4 overflow-x-auto">
              {p.photos.map((u, i) => (
                <button key={u} onClick={() => setActive(i)} data-testid={`profile-thumb-${i}`} className={`w-20 h-20 rounded-xl overflow-hidden border-2 shrink-0 ${i === active ? "border-amber-400" : "border-transparent"}`}>
                  <img src={photoUrl(u)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-6">
          {p.is_ai ? <AiBadge /> : <MemberBadge />}
          {compatibility(profile, p) && <span data-testid="profile-compat-score" className="ml-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-rose-600/20 text-rose-200 border border-rose-500/40">{compatibility(profile, p)}% {t("match")}</span>}
          <h1 className="font-display text-5xl font-semibold mt-4" data-testid="profile-name">{p.name}</h1>
          <p className="text-slate-300 mt-2 flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-400" /> {p.age} {t("yrs")} · {p.city}</p>
          <p className="text-slate-300 mt-6 leading-relaxed" data-testid="profile-about">{p.about}</p>
          {p.is_ai && <p className="text-xs text-purple-300/80 mt-3">{t("aiNote")}</p>}
          <div className="mt-8">
            <Fact label={t("country")} value={p.country} /><Fact label={t("openIntl")} value={p.open_to_international ? t("yes") : p.country ? t("no") : null} />
            <Fact label={t("profession")} value={p.profession} /><Fact label={t("education")} value={p.education} />
            <Fact label={t("religion")} value={p.religion} /><Fact label={t("tongue")} value={p.mother_tongue} />
            <Fact label={t("height")} value={p.height} /><Fact label={t("diet")} value={p.diet} />
          </div>
          {p.interests?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">{p.interests.map((x) => <span key={x} className="px-3 py-1 rounded-full text-xs bg-amber-500/10 text-amber-200 border border-amber-500/25">{x}</span>)}</div>
          )}
          {!isMine && (
            <div className="flex flex-wrap gap-3 mt-10">
              <button onClick={startChat} data-testid="profile-chat-button" className="btn-gold px-8 py-3.5 rounded-full font-medium flex items-center gap-2">
                <MessageCircleHeart className="w-5 h-5" /> {t("sendMsg")} · ₹{crossBorder(profile, p) ? 30 : 1}
              </button>
              <InterestButton pid={p.id} />
              <ShortlistButton pid={p.id} label />
            </div>
          )}
          {!p.is_ai && p.chat_requires_interest !== false && !isMine && <p className="text-xs text-slate-500 mt-3">{p.name.split(" ")[0]} {t("needsInterest")}</p>}
          <a data-testid="profile-share-whatsapp" target="_blank" rel="noreferrer"
            href={`https://wa.me/?text=${encodeURIComponent(`See this profile on Vivaah Royale: ${p.name}, ${p.age}, ${p.profession} from ${p.city} ${window.location.origin}/profile/${p.id}${account?.ref_code ? `?ref=${account.ref_code}` : ""}`)}`}
            className="inline-flex items-center gap-2 mt-6 text-sm text-emerald-300 hover:text-emerald-200"><Share2 className="w-4 h-4" /> {t("share")}</a>
        </div>
      </div>
    </div>
  );
}
